import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { resolveClinicForUser } from '../lib/clinicResolver';
import Sidebar from './Sidebar';
import LoadingSpinner from './LoadingSpinner';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const ClientsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPetForm, setShowPetForm] = useState(false);
  const [expandedClients, setExpandedClients] = useState(new Set());
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [petForm, setPetForm] = useState({
    name: '',
    species: '',
    species_other: '',
    breed: '',
    birth_date: '',
    weight: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [clientsPerPage] = useState(10);
  const [totalClients, setTotalClients] = useState(0);

  // Toast notification state
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  // Refs for cleanup
  const toastTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Debounced search for smoother UX
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    checkUser();
  }, []);

  // Reset to page 1 when search changes
  useEffect(() => {
    if (debouncedSearchTerm !== '') {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (clinic?.id) {
      fetchClients();
    }
  }, [clinic?.id, currentPage, debouncedSearchTerm]);

  const showToast = (message, type = 'success') => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToast({ show: true, message, type });
    toastTimeoutRef.current = setTimeout(() => {
      setToast({ show: false, message: '', type: 'success' });
    }, 4000);
  };

  // Cleanup effect for toast timeout and abort controller
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error('❌ Error getting user:', userError);
        navigate('/login');
        return;
      }
      
      if (!user) {
        if (import.meta.env.DEV) console.log('❌ No user found, redirecting to login');
        navigate('/login');
        return;
      }

      setUser(user);

      // Fetch clinic data (supports both owners and vet employees)
      const { clinic: clinicData, error: clinicResolveError } = await resolveClinicForUser(supabase, user.id);

      if (clinicResolveError || !clinicData) {
        console.error('Error resolving clinic:', clinicResolveError);
      } else {
        setClinic(clinicData);
      }
    } catch (error) {
      console.error('❌ Unexpected error in checkUser:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    // Add a safety timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (import.meta.env.DEV) console.log('⏰ Fetch timeout reached, setting loading to false');
      setLoading(false);
    }, 10000); // 10 seconds timeout

    try {
      if (import.meta.env.DEV) console.log('🔍 Fetching clients for clinic_id:', clinic.id);
      if (import.meta.env.DEV) console.log('🔍 Current user:', user?.id);
      if (import.meta.env.DEV) console.log('🔍 Current page:', currentPage);

      const searchTrimmed = debouncedSearchTerm.trim();
      let ownerFilter = null;

      // If searching, first find pets matching the search term
      if (searchTrimmed) {
        const { data: matchingPets } = await supabase
          .from('pets')
          .select('owner_id')
          .ilike('name', `%${searchTrimmed}%`)
          .is('deleted_at', null);

        const petOwnerIds = matchingPets?.length
          ? [...new Set(matchingPets.map(p => p.owner_id))]
          : [];

        ownerFilter = `first_name.ilike.%${searchTrimmed}%,last_name.ilike.%${searchTrimmed}%,phone.ilike.%${searchTrimmed}%`;
        if (petOwnerIds.length > 0) {
          ownerFilter += `,id.in.(${petOwnerIds.join(',')})`;
        }
      }

      // Build count query
      let countQuery = supabase
        .from('owners')
        .select('*', { count: 'exact', head: true })
        .eq('clinic_id', clinic.id)
        .is('deleted_at', null);
      if (ownerFilter) countQuery = countQuery.or(ownerFilter);

      // Build data query
      const from = (currentPage - 1) * clientsPerPage;
      const to = from + clientsPerPage - 1;
      let dataQuery = supabase
        .from('owners')
        .select('*, pets (id, name, species, breed, birth_date, weight, deleted_at)')
        .eq('clinic_id', clinic.id)
        .is('deleted_at', null);
      if (ownerFilter) dataQuery = dataQuery.or(ownerFilter);

      // Run count + data in parallel
      const [countResult, dataResult] = await Promise.all([
        countQuery,
        dataQuery.order('created_at', { ascending: false }).range(from, to)
      ]);

      if (countResult.error) throw countResult.error;
      if (dataResult.error) throw dataResult.error;

      setTotalClients(countResult.count || 0);

      // Filter out soft-deleted pets client-side
      const clientsData = (dataResult.data || []).map(client => ({
        ...client,
        pets: (client.pets || []).filter(pet => !pet.deleted_at)
      }));

      setClients(clientsData);
      setLoading(false);
      clearTimeout(timeoutId);
    } catch (error) {
      // Ignore aborted requests
      if (error.name === 'AbortError') {
        clearTimeout(timeoutId);
        return;
      }
      console.error('Error fetching clients:', error);
      showToast('Σφάλμα κατά τη φόρτωση των πελατών.', 'error');
      setLoading(false); // Set loading to false even on error
      clearTimeout(timeoutId); // Clear the timeout
    }
  };

  const toggleClientExpansion = (clientId) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const handlePetFormChange = (e) => {
    const { name, value } = e.target;
    setPetForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const addPetToClient = async () => {
    if (!petForm.name.trim() || (!petForm.species.trim() && !(petForm.species === 'other' && petForm.species_other.trim()))) {
      showToast('Παρακαλώ συμπληρώστε τουλάχιστον το όνομα και το είδος του κατοικιδίου.', 'error');
      return;
    }

    setSubmitting(true);

    try {
      const finalSpecies = petForm.species === 'other' ? petForm.species_other.trim() : petForm.species.trim();
      const { error } = await supabase
        .from('pets')
        .insert({
          name: petForm.name.trim(),
          species: finalSpecies,
          breed: petForm.breed.trim() || null,
          birth_date: petForm.birth_date || null,
          weight: petForm.weight ? parseFloat(petForm.weight) : null,
          owner_id: selectedClient.id
        });

      if (error) throw error;

      showToast(`Το κατοικίδιο "${petForm.name}" προστέθηκε στον πελάτη "${selectedClient.first_name} ${selectedClient.last_name}"!`, 'success');

      // Reset form and refresh clients
      resetPetForm();

      // Refresh clients to show new pet
      await fetchClients();
      
    } catch (error) {
      console.error('Error adding pet:', error);
      showToast('Σφάλμα κατά την προσθήκη του κατοικιδίου. Παρακαλώ δοκιμάστε ξανά.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      return date.toLocaleDateString('el-GR');
    } catch {
      return '-';
    }
  };

  const formatWeight = (weight) => {
    if (!weight) return '-';
    return `${weight} kg`;
  };

  // Helper to reset pet form state
  const resetPetForm = () => {
    setPetForm({ name: '', species: '', species_other: '', breed: '', birth_date: '', weight: '' });
    setShowPetForm(false);
    setSelectedClient(null);
  };

  // Pagination functions
  const goToPage = (page) => {
    if (import.meta.env.DEV) console.log('🔄 Going to page:', page);
    setCurrentPage(page);
    setExpandedClients(new Set()); // Reset expanded clients when changing pages
    setLoading(true); // Show loading while fetching new page
  };

  const goToNextPage = () => {
    if (currentPage < Math.ceil(totalClients / clientsPerPage)) {
      goToPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      goToPage(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const totalPages = Math.ceil(totalClients / clientsPerPage);
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination with ellipsis
      if (currentPage <= 3) {
        // Near start
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near end
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Middle
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  // Close modal handler with Escape key support
  const closeModal = useCallback(() => {
    resetPetForm();
  }, []);

  // Escape key handler for modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showPetForm) {
        closeModal();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showPetForm, closeModal]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen text-white flex flex-col lg:flex-row" style={{ backgroundColor: '#1A202C', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, background: 'radial-gradient(800px 800px at 10% -10%, rgba(16,185,129,0.12), transparent), radial-gradient(800px 800px at 110% 10%, rgba(59,130,246,0.08), transparent)' }} className="pointer-events-none" />
      <Sidebar isSidebarOpen={isSidebarOpen} setIsSidebarOpen={setIsSidebarOpen} />

      {/* Main Content */}
      <div className="flex-1 p-4 lg:p-8">
        {/* Toast Notification */}
        {toast.show && (
          <div className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${
            toast.show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          }`}>
            <div
              className="flex items-center px-5 py-4 rounded-xl border max-w-sm"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.95) 0%, rgba(31,41,55,0.95) 100%)',
                backdropFilter: 'blur(10px)',
                borderColor: toast.type === 'success' ? '#10B981' : toast.type === 'error' ? '#EF4444' : '#3B82F6',
                boxShadow: `0 10px 25px rgba(0,0,0,0.3), 0 0 20px ${
                  toast.type === 'success' ? 'rgba(16,185,129,0.2)' :
                  toast.type === 'error' ? 'rgba(239,68,68,0.2)' : 'rgba(59,130,246,0.2)'
                }`
              }}
            >
              <div className="flex-shrink-0 mr-3">
                {toast.type === 'success' && (
                  <svg className="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                )}
                {toast.type === 'error' && (
                  <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                )}
                {toast.type === 'info' && (
                  <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                  </svg>
                )}
              </div>
              <div className="text-white font-medium text-sm">{toast.message}</div>
              <button
                onClick={() => setToast({ show: false, message: '', type: 'success' })}
                className="ml-4 text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 lg:mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">Πελάτες</h1>
            <p className="text-gray-400 text-sm lg:text-base">
              Διαχείριση πελατών και κατοικιδίων
            </p>
          </div>
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-xl"
            style={{
              background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(6,182,212,0.15) 100%)',
              border: '1px solid rgba(16,185,129,0.3)'
            }}
          >
            <span className="text-emerald-400 font-medium text-sm">
              {totalClients} πελάτες
            </span>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6 lg:mb-8">
          <div className="relative">
            <input
              type="text"
              placeholder="Αναζήτηση με όνομα, επώνυμο, κινητό ή όνομα κατοικιδίου..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                // Do not toggle the global loading spinner while typing for a smoother UX
              }}
              className="w-full px-4 py-3 pl-12 rounded-lg border text-white"
              style={{ backgroundColor: '#111827', borderColor: '#374151' }}
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Clients List */}
        <div className="space-y-4 lg:space-y-6">
          {clients.length > 0 ? (
            clients.map((client) => (
              <div
                key={client.id}
                className="rounded-xl overflow-hidden border transition-all duration-300 hover:border-emerald-500/30"
                style={{
                  background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
                  borderColor: '#374151',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
                }}
              >
                {/* Client Header */}
                <div className="px-4 lg:px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(55,65,81,0.65), rgba(31,41,55,0.65))' }}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0">
                    <button
                      onClick={() => toggleClientExpansion(client.id)}
                      className="flex-1 text-left hover:bg-gray-800/60 rounded-lg p-2 transition-all duration-200 hover:translate-y-[-1px]"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-base lg:text-lg font-semibold text-white">
                            {client.first_name} {client.last_name}
                          </h3>
                          <div className="flex flex-col lg:flex-row lg:items-center lg:space-x-4 space-y-1 lg:space-y-0 text-xs lg:text-sm text-gray-300 mt-1">
                            {client.email && (
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                                </svg>
                                {client.email}
                              </span>
                            )}
                            {client.phone && (
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                                </svg>
                                {client.phone}
                              </span>
                            )}
                            {client.address && (
                              <span className="flex items-center">
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 111.314 0z"></path>
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                                </svg>
                                {client.address}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className="text-xs lg:text-sm text-gray-300">
                            {client.pets?.length || 0} κατοικίδια
                          </span>
                          <svg 
                            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                              expandedClients.has(client.id) ? 'rotate-180' : ''
                            }`} 
                            fill="none" 
                            stroke="currentColor" 
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                          </svg>
                        </div>
                      </div>
                    </button>
                    <button
                      onClick={() => {
                        setSelectedClient(client);
                        setShowPetForm(true);
                      }}
                      className="w-full lg:w-auto px-5 py-2.5 rounded-lg font-medium text-white transition-transform duration-200 hover:scale-[1.02] text-sm"
                      style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                    >
                      + Προσθήκη Κατοικιδίου
                    </button>
                  </div>
                </div>

                {/* Pets Section - Collapsible */}
                {expandedClients.has(client.id) && (
                  <div className="p-4 lg:p-6">
                    <h4 className="text-sm lg:text-md font-medium text-white mb-4">
                      Κατοικίδια ({client.pets?.length || 0})
                    </h4>
                    
                    {client.pets && client.pets.length > 0 ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                        {client.pets.map((pet) => (
                          <div key={pet.id} className="p-4 rounded-lg border" style={{ backgroundColor: '#1F2937', borderColor: '#374151' }}>
                            <div className="mb-3">
                              <h5 className="text-white font-medium">{pet.name}</h5>
                            </div>
                            <div className="space-y-2 text-xs lg:text-sm text-gray-300 mb-4">
                              <div><span className="font-medium">Είδος:</span> {pet.species}</div>
                              {pet.breed && <div><span className="font-medium">Φυλή:</span> {pet.breed}</div>}
                              {pet.birth_date && <div><span className="font-medium">Γεννήθηκε:</span> {formatDate(pet.birth_date)}</div>}
                              {pet.weight && <div><span className="font-medium">Βάρος:</span> {formatWeight(pet.weight)}</div>}
                            </div>
                            
                            {/* Appointments Buttons */}
                            <div className="flex flex-col lg:flex-row space-y-2 lg:space-y-0 lg:space-x-2">
                              <button
                                onClick={() => navigate(`/pet-medical-history/${pet.id}`)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-white rounded-lg transition-transform duration-200 hover:scale-[1.02]"
                                style={{ backgroundColor: '#3B82F6' }}
                              >
                                📋 Ιστορικό
                              </button>
                              <button
                                onClick={() => navigate(`/new-medical-record/${pet.id}`)}
                                className="flex-1 px-3 py-2 text-xs font-medium text-white rounded-lg transition-transform duration-200 hover:scale-[1.02]"
                                style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                              >
                                ✏️ Νέο Ραντεβού
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-400">
                        <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 00-2 2v7m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                        </svg>
                        <p className="text-sm lg:text-base">Δεν υπάρχουν κατοικίδια για αυτόν τον πελάτη</p>
                        <p className="text-xs lg:text-sm mt-1">Κάντε κλικ στο κουμπί "Προσθήκη Κατοικιδίου" για να προσθέσετε ένα</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div
              className="rounded-xl p-8 border text-center"
              style={{
                background: 'linear-gradient(135deg, #111827 0%, #1F2937 100%)',
                borderColor: '#374151',
                boxShadow: '0 10px 25px rgba(0,0,0,0.3)'
              }}
            >
              <div
                className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: 'rgba(16,185,129,0.15)' }}
              >
                <svg className="w-8 h-8 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white mb-2">Δεν βρέθηκαν πελάτες</h3>
              <p className="text-gray-400 mb-6">
                {searchTerm ? 'Δοκιμάστε να αλλάξετε τους όρους αναζήτησης' : 'Ξεκινήστε προσθέτοντας τον πρώτο σας πελάτη'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => navigate('/new-client')}
                  className="px-6 py-3 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                >
                  Προσθήκη Πρώτου Πελάτη
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalClients > clientsPerPage && (
          <div className="mt-8 flex flex-col items-center space-y-4">
            {/* Page Info */}
            <div className="text-sm text-gray-400">
              Εμφάνιση {((currentPage - 1) * clientsPerPage) + 1} - {Math.min(currentPage * clientsPerPage, totalClients)} από {totalClients} πελάτες
            </div>
            
            {/* Pagination Controls */}
            <div className="flex items-center space-x-2">
              {/* Previous Button */}
              <button
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage === 1
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'text-white hover:bg-gray-700'
                }`}
                style={{ backgroundColor: currentPage === 1 ? '#4A5568' : '#2D3748' }}
              >
                ← Προηγούμενη
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {getPageNumbers().map((page, index) => (
                  <button
                    key={index}
                    onClick={() => typeof page === 'number' ? goToPage(page) : null}
                    disabled={typeof page !== 'number'}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      typeof page === 'number'
                        ? page === currentPage
                          ? 'text-white'
                          : 'text-gray-300 hover:text-white hover:bg-gray-700'
                        : 'text-gray-500 cursor-default'
                    }`}
                    style={{ 
                      backgroundColor: typeof page === 'number' && page === currentPage ? '#10B981' : '#2D3748' 
                    }}
                  >
                    {page}
                  </button>
                ))}
              </div>
              
              {/* Next Button */}
              <button
                onClick={goToNextPage}
                disabled={currentPage >= Math.ceil(totalClients / clientsPerPage)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  currentPage >= Math.ceil(totalClients / clientsPerPage)
                    ? 'text-gray-500 cursor-not-allowed'
                    : 'text-white hover:bg-gray-700'
                }`}
                style={{ backgroundColor: currentPage >= Math.ceil(totalClients / clientsPerPage) ? '#4A5568' : '#2D3748' }}
              >
                Επόμενη →
              </button>
            </div>
          </div>
        )}

        {/* Add Pet Modal */}
        {showPetForm && selectedClient && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fadeIn"
            onClick={(e) => e.target === e.currentTarget && closeModal()}
          >
            <div
              className="rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border animate-slideUp"
              style={{
                background: 'linear-gradient(135deg, rgba(17,24,39,0.98) 0%, rgba(31,41,55,0.98) 100%)',
                backdropFilter: 'blur(20px)',
                borderColor: 'rgba(16,185,129,0.3)',
                boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 40px rgba(16,185,129,0.1)'
              }}
            >
              {/* Modal Header */}
              <div className="px-6 py-4 border-b" style={{ borderColor: '#374151', background: 'linear-gradient(90deg, rgba(16,185,129,0.1), rgba(6,182,212,0.05))' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.2), rgba(6,182,212,0.2))' }}>
                      <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white">Νέο Κατοικίδιο</h3>
                      <p className="text-sm text-gray-400">{selectedClient?.first_name} {selectedClient?.last_name}</p>
                    </div>
                  </div>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-gray-700/50 transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <form onSubmit={(e) => { e.preventDefault(); addPetToClient(); }} className="p-6">
                <div className="space-y-4">
                  {/* Row 1: Name and Species */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Όνομα *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={petForm.name}
                        onChange={handlePetFormChange}
                        required
                        autoFocus
                        className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                        style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}
                        placeholder="π.χ. Μπόμπι"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Είδος *
                      </label>
                      <select
                        name="species"
                        value={petForm.species}
                        onChange={handlePetFormChange}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                        style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}
                      >
                        <option value="">Επιλέξτε</option>
                        <option value="Σκύλος">Σκύλος</option>
                        <option value="Γάτα">Γάτα</option>
                        <option value="other">Άλλο</option>
                      </select>
                    </div>
                  </div>

                  {/* Custom Species */}
                  {petForm.species === 'other' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Είδος (προσαρμοσμένο) *
                      </label>
                      <input
                        type="text"
                        name="species_other"
                        value={petForm.species_other}
                        onChange={handlePetFormChange}
                        required
                        className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                        style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}
                        placeholder="π.χ. Κουνέλι, Πουλί, Χάμστερ"
                      />
                    </div>
                  )}

                  {/* Row 2: Breed */}
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Φυλή
                    </label>
                    <input
                      type="text"
                      name="breed"
                      value={petForm.breed}
                      onChange={handlePetFormChange}
                      className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                      style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}
                      placeholder="π.χ. Γερμανικός Ποιμενικός"
                    />
                  </div>

                  {/* Row 3: Birth Date and Weight */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Ημ. Γέννησης
                      </label>
                      <DatePicker
                        selected={petForm.birth_date ? new Date(petForm.birth_date + 'T00:00:00') : null}
                        onChange={(date) => {
                          if (date) {
                            const yyyy = date.getFullYear();
                            const mm = String(date.getMonth() + 1).padStart(2, '0');
                            const dd = String(date.getDate()).padStart(2, '0');
                            setPetForm(prev => ({ ...prev, birth_date: `${yyyy}-${mm}-${dd}` }));
                          } else {
                            setPetForm(prev => ({ ...prev, birth_date: '' }));
                          }
                        }}
                        maxDate={new Date()}
                        dateFormat="dd/MM/yyyy"
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        scrollableYearDropdown
                        yearDropdownItemNumber={40}
                        placeholderText="Επιλέξτε ημερομηνία"
                        isClearable
                        className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                        calendarClassName="dark-datepicker"
                        wrapperClassName="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Βάρος (kg)
                      </label>
                      <input
                        type="number"
                        name="weight"
                        value={petForm.weight}
                        onChange={handlePetFormChange}
                        step="0.1"
                        min="0"
                        className="w-full px-4 py-2.5 rounded-lg border text-white focus:outline-none focus:ring-2 focus:ring-emerald-400/30 focus:border-emerald-500/50 transition-all"
                        style={{ backgroundColor: 'rgba(31,41,55,0.8)', borderColor: '#374151' }}
                        placeholder="0.0"
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 mt-6 pt-4 border-t" style={{ borderColor: '#374151' }}>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-5 py-2.5 rounded-lg font-medium text-gray-300 transition-all duration-200 hover:bg-gray-600/50"
                    style={{ backgroundColor: 'rgba(55,65,81,0.5)' }}
                  >
                    Ακύρωση
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2.5 rounded-lg font-medium text-white transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
                    style={{ background: 'linear-gradient(90deg, #10B981, #059669)' }}
                  >
                    {submitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Προσθήκη...
                      </span>
                    ) : 'Προσθήκη Κατοικιδίου'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Mobile overlay to close sidebar when clicking outside */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default ClientsPage; 